import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

// Each entry maps to `pricingFaq.items.<id>.{q,a}` in the locale files.
// Order is the canonical display order; English fallbacks live in en.ts.
// Answers containing newlines render as a bulleted list; a leading
// "Label:" (or fullwidth "：") on a line is emphasized.
function AnswerBody({ answer }: { answer: string }) {
  const lines = answer
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length <= 1) return <>{answer}</>;
  return (
    <ul className="list-disc space-y-2 pl-5">
      {lines.map((line, index) => {
        const match = line.match(/^([^:：]{1,40}?)\s*([:：])\s*(.*)$/);
        return (
          <li key={index}>
            {match ? (
              <>
                <span className="font-semibold text-gray-300">
                  {match[1]}
                  {match[2]}
                </span>{" "}
                {match[3]}
              </>
            ) : (
              line
            )}
          </li>
        );
      })}
    </ul>
  );
}

const PRICING_FAQ_IDS = [
  "autorenew",
  "changePlan",
  "howMany",
  "commercial",
  "ownership",
  "howCreditsWork",
  "addonPackages",
  "buyExtra",
  "support",
  "refundPolicy",
] as const;

type PricingFAQSectionProps = {
  compact?: boolean;
  className?: string;
};

export default function PricingFAQSection({
  compact = false,
  className,
}: PricingFAQSectionProps) {
  const { t } = useTranslation();
  return (
    <section
      className={cn(
        compact ? "mx-auto max-w-4xl" : "mx-auto max-w-4xl py-4",
        className,
      )}
      aria-labelledby={
        compact ? "pricing-modal-faq-title" : "pricing-faq-title"
      }
    >
      <h2
        id={compact ? "pricing-modal-faq-title" : "pricing-faq-title"}
        className={cn(
          "text-center font-bold text-white",
          compact ? "mb-3 text-xl sm:text-2xl" : "mb-8 text-3xl md:text-4xl",
        )}
      >
        {t("pricingFaq.sectionTitle")}
      </h2>

      <AccordionPrimitive.Root type="single" collapsible>
        {PRICING_FAQ_IDS.map((id, index) => {
          const question = t(`pricingFaq.items.${id}.q`);
          const answer = t(`pricingFaq.items.${id}.a`);
          return (
            <AccordionPrimitive.Item
              key={id}
              value={`pricing-faq-${index}`}
              className="border-b border-white/10"
            >
              <AccordionPrimitive.Header className="flex">
                <AccordionPrimitive.Trigger
                  className={cn(
                    "flex w-full items-center justify-between gap-6 text-left font-semibold text-white outline-none transition-colors hover:text-pink-200 focus-visible:ring-2 focus-visible:ring-pink-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-950 [&[data-state=open]>svg]:rotate-45",
                    compact ? "py-4 text-sm sm:text-base" : "py-6 text-lg",
                  )}
                >
                  <span>{question}</span>
                  <Plus
                    className="h-5 w-5 shrink-0 text-white transition-transform duration-200"
                    aria-hidden="true"
                  />
                </AccordionPrimitive.Trigger>
              </AccordionPrimitive.Header>
              <AccordionPrimitive.Content className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                <div
                  className={cn(
                    "max-w-3xl leading-relaxed text-gray-400",
                    compact ? "pb-4 text-xs sm:text-sm" : "pb-6 text-sm",
                  )}
                >
                  <AnswerBody answer={answer} />
                </div>
              </AccordionPrimitive.Content>
            </AccordionPrimitive.Item>
          );
        })}
      </AccordionPrimitive.Root>
    </section>
  );
}
